// Lightweight in-app Help Center drawer. Topic-based, expandable content,
// designed for future content growth. Accessible + keyboard dismissable.

import React, { useState } from 'react';
import { X, ChevronRight, BookOpen, Search, CheckCircle2, LifeBuoy } from 'lucide-react';
import { HELP_TOPICS } from './guidanceContent';
import { trackGuidance } from '@/lib/onboarding';
import { useEscapeToClose } from './ContextualHelp';

interface HelpCenterProps {
  open: boolean;
  onClose: () => void;
}

const HelpCenter: React.FC<HelpCenterProps> = ({ open, onClose }) => {
  const [active, setActive] = useState<string | null>(null);
  const [query, setQuery] = useState('');

  useEscapeToClose(open, onClose);

  if (!open) return null;

  const q = query.trim().toLowerCase();
  const topics = q
    ? HELP_TOPICS.filter(
        (t) =>
          t.title.toLowerCase().includes(q) ||
          t.summary.toLowerCase().includes(q) ||
          t.points.some((p) => p.toLowerCase().includes(q)),
      )
    : HELP_TOPICS;

  const openTopic = (id: string) => {
    setActive((cur) => (cur === id ? null : id));
    if (active !== id) trackGuidance('help_topic_viewed', { topic: id });
  };

  return (
    <div className="fixed inset-0 z-[95] font-eds-body" role="dialog" aria-modal="true" aria-label="Help center">
      <div className="absolute inset-0 bg-eds-forest/50 backdrop-blur-[2px]" onClick={onClose} />
      <aside className="absolute right-0 top-0 h-full w-full sm:w-[27rem] bg-eds-surface shadow-eds-md flex flex-col">
        {/* Branded header */}
        <header className="relative overflow-hidden bg-eds-forest px-5 pt-5 pb-4 text-white">
          <div className="absolute -top-10 -right-6 w-40 h-40 rounded-eds-pill bg-eds-emerald/15 blur-3xl" aria-hidden="true" />
          <div className="relative flex items-start justify-between">
            <div className="flex items-center gap-2.5">
              <span className="w-9 h-9 rounded-eds-md bg-white/10 border border-white/15 flex items-center justify-center text-eds-mint">
                <BookOpen className="w-[18px] h-[18px]" />
              </span>
              <div className="leading-tight">
                <h2 className="font-eds-display text-base font-bold">Help Center</h2>
                <p className="text-[11px] text-white/60">Guides, how-tos and answers</p>
              </div>
            </div>
            <button
              type="button"
              aria-label="Close help center"
              onClick={onClose}
              className="text-white/70 hover:text-white hover:bg-white/10 rounded-eds-md p-1 focus:outline-none focus-visible:ring-2 focus-visible:ring-eds-mint/60"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="relative mt-4">
            <Search className="w-4 h-4 text-white/50 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search help topics…"
              className="w-full pl-9 pr-3 py-2.5 rounded-eds-md bg-white/10 border border-white/15 text-sm text-white placeholder:text-white/50 focus:outline-none focus:ring-2 focus:ring-eds-mint/50 focus:bg-white/15"
            />
          </div>
        </header>

        <div className="flex-1 overflow-y-auto px-3.5 py-4 space-y-2 bg-eds-bg">
          {topics.length === 0 ? (
            <div className="text-center px-4 py-12">
              <div className="mx-auto w-11 h-11 rounded-eds-pill bg-eds-surface-2 border border-eds-border flex items-center justify-center text-eds-muted-fg mb-3">
                <Search className="w-5 h-5" />
              </div>
              <p className="text-sm text-eds-fg font-medium">No topics match “{query}”.</p>
              <p className="text-sm font-medium text-eds-muted-fg mt-1">Try a different keyword.</p>
            </div>
          ) : (
            topics.map((t) => {
              const expanded = active === t.id;
              return (
                <div
                  key={t.id}
                  className={`rounded-eds-lg border bg-eds-surface overflow-hidden transition ${expanded ? 'border-eds-emerald/40 shadow-eds-sm' : 'border-eds-border'}`}
                >
                  <button
                    type="button"
                    onClick={() => openTopic(t.id)}
                    className="w-full flex items-center gap-3 px-4 py-3.5 text-left hover:bg-eds-surface-2/60 transition focus:outline-none focus-visible:ring-2 focus-visible:ring-eds-ring rounded-eds-lg"
                    aria-expanded={expanded}
                  >
                    <span className={`w-8 h-8 flex-shrink-0 rounded-eds-md flex items-center justify-center transition ${expanded ? 'bg-eds-emerald text-white' : 'bg-eds-success/10 border border-eds-success/20 text-eds-emerald-2'}`}>
                      <BookOpen className="w-4 h-4" />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block text-sm font-semibold text-eds-fg">{t.title}</span>
                      <span className="block text-sm font-medium text-eds-muted-fg mt-0.5">{t.summary}</span>
                    </span>
                    <ChevronRight
                      className={`w-4 h-4 flex-shrink-0 text-eds-muted-fg transition-transform ${expanded ? 'rotate-90 text-eds-emerald-2' : ''}`}
                    />
                  </button>
                  {expanded && (
                    <ul className="px-4 pb-4 pt-1 space-y-2.5 border-t border-eds-border">
                      {t.points.map((p, i) => (
                        <li key={i} className="text-sm text-eds-muted-fg flex gap-2.5 pt-1">
                          <CheckCircle2 className="w-4 h-4 text-eds-emerald-2 mt-0.5 flex-shrink-0" />
                          <span className="leading-relaxed">{p}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              );
            })
          )}
        </div>

        <footer className="px-5 py-4 border-t border-eds-border bg-eds-surface">
          <a
            href="/contact"
            className="flex items-center gap-3 rounded-eds-md p-3 bg-eds-surface-2/60 hover:bg-eds-surface-2 border border-eds-border transition group"
          >
            <span className="w-9 h-9 flex-shrink-0 rounded-eds-md bg-eds-forest flex items-center justify-center text-eds-mint">
              <LifeBuoy className="w-[18px] h-[18px]" />
            </span>
            <span className="min-w-0">
              <span className="block text-sm font-semibold text-eds-fg">Still need help?</span>
              <span className="block text-sm font-medium text-eds-muted-fg">Contact the GIISDEC team</span>
            </span>
            <ChevronRight className="w-4 h-4 ml-auto text-eds-muted-fg group-hover:translate-x-0.5 transition" />
          </a>
        </footer>
      </aside>
    </div>
  );
};

export default HelpCenter;
